// global/GlobalStyle.ts
export const CommonClasses = {
  // Layout
  flex: "flex",
  flexCol: "flex flex-col",
  flexRow: "flex flex-row",
  flexCenter: "flex justify-center items-center",
  flexBetween: "flex justify-between items-center",
  flexStart: "flex justify-start items-center",

  // Backgrounds
  bgLight: "bg-lightBackground",
  bgDark: "bg-darkBackground",
  bgPrimary: "bg-primary",
  bgSecondary: "bg-secondary",
  bgWhite: "bg-white",
  bgBlack: "dark:bg-[#121212]",

  // Text
  textWhite: "text-white",
  textBlack: "text-black",
  textPrimary: "text-primary",
  textSecondary: "text-secondary",
  textGray: "text-gray-600 dark:text-gray-400",
  textGrayStrong: "text-gray-800 dark:text-gray-200",
  textXs: "text-xs",
  textSm: "text-sm",
  textBase: "text-base",
  textLg: "text-lg",
  textXl: "text-xl",

  // Borders & Shapes
  rounded: "rounded-lg",
  roundedFull: "rounded-full",
  border: "border border-gray-200 dark:border-gray-700",

  // Effects
  transition: "transition-all ease-in-out duration-300",
  shadow: "shadow",
  shadowMd: "shadow-md",

  // Animations
  animateSlideInRight: "animate-slide-in-right",
  animateFadeIn: "animate-fade-in",

  // New button variants
  buttonIcon:
    "p-2 rounded-lg transition-all duration-300 flex items-center justify-center",
};

const GlobalStyles = {
  // Layout Containers
  container: `min-h-screen ${CommonClasses.bgLight} dark:${CommonClasses.bgDark} ${CommonClasses.transition} ${CommonClasses.flexCol} pt-16 px-5 lg:px-8 pb-10 gap-6`,
  containerCenter: `h-screen ${CommonClasses.bgLight} dark:${CommonClasses.bgDark} ${CommonClasses.transition} ${CommonClasses.flexCenter} pt-16 px-5 lg:px-8 pb-10`,

  // Sections
  sectionWrap: `w-full bg-white dark:bg-gray-800 rounded-lg p-6 transition-all border border-gray-200 dark:border-gray-700 shadow-md`,
  sectionDivider: "w-full gap-5 flex max-lg:flex-col",
  sectionDividerGridFour:
    "w-full gap-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4",

  // ======== FORMS =========
  formContainer: `${CommonClasses.shadow} ${CommonClasses.rounded} ${CommonClasses.transition} w-full md:w-1/2 lg:w-1/3 bg-white dark:bg-[#121212]`,
  formHeader: `p-6 border-b border-gray-200 dark:border-gray-800 ${CommonClasses.flexCol} items-center gap-2 ${CommonClasses.transition}`,
  formHeaderTitle: `text-2xl font-bold ${CommonClasses.textGrayStrong} ${CommonClasses.transition}`,
  formHeaderSubtitle: `text-sm ${CommonClasses.textGray} ${CommonClasses.transition} text-center`,
  formGroup: `${CommonClasses.flexCol} mb-4`,
  inputWrapper: `w-full p-3 gap-3 ${CommonClasses.flexStart} ${CommonClasses.rounded} ${CommonClasses.transition} border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent`,
  input: `w-full outline-none bg-transparent ${CommonClasses.textBase} ${CommonClasses.transition} ${CommonClasses.textGrayStrong} placeholder-gray-500 dark:placeholder-gray-400`,
  inputIcon: `w-5 h-5 text-gray-500`,
  label: `block ${CommonClasses.textSm} font-medium text-gray-700 dark:text-gray-300 mb-2`,

  // Buttons
  buttonPrimary: `bg-primary hover:bg-primary/90 w-full py-3 ${CommonClasses.textWhite} ${CommonClasses.rounded} ${CommonClasses.transition} ${CommonClasses.flexCenter} ${CommonClasses.shadow} font-medium`,
  buttonSecondary: `bg-secondary hover:bg-secondary/90 w-full py-3 ${CommonClasses.textWhite} ${CommonClasses.rounded} ${CommonClasses.transition} ${CommonClasses.flexCenter} ${CommonClasses.shadow} font-medium`,
  buttonOutline: `border-2 border-primary text-primary hover:bg-primary hover:text-white w-full py-3 ${CommonClasses.rounded} ${CommonClasses.transition} ${CommonClasses.flexCenter} ${CommonClasses.shadow} font-medium`,

  // New button variants
  buttonIconPrimary: `${CommonClasses.buttonIcon} bg-primary hover:bg-primary/90 text-white shadow`,
  buttonIconOutline: `${CommonClasses.buttonIcon} border-2 border-primary text-primary hover:bg-primary hover:text-white`,
  buttonIconDanger: `${CommonClasses.buttonIcon} border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white`,

  // Links
  link: `hover:text-primary/80 ${CommonClasses.transition} ${CommonClasses.textSm} text-gray-700 dark:text-gray-300 font-medium`,
  linkSmall: `hover:opacity-70 ${CommonClasses.transition} ${CommonClasses.textXs} ${CommonClasses.textWhite}`,
  linkAccent: `text-primary hover:underline ${CommonClasses.transition}`,

  // Alerts
  alert: `p-4 ${CommonClasses.rounded} ${CommonClasses.flexBetween} ${CommonClasses.transition} ${CommonClasses.shadowMd}`,
  alertSuccess: `bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200`,
  alertError: `bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200`,
  alertWarning: `bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200`,
  alertInfo: `bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200`,

  // Table styles
  tableContainer: `bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700`,
  table: `w-full`,
  tableHeader: `border-b border-gray-200 dark:border-gray-700`,
  tableHeaderCell: `text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300`,
  tableBody: ``,
  tableRow: `border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200`,
  tableCell: `py-3 px-4`,

  // Modal overlay fix
  modalOverlay: `fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto`,
  modalContainer: `bg-white dark:bg-gray-800 rounded-lg shadow-md w-full max-w-2xl my-8`,
};

export default GlobalStyles;
